import { Component } from '@theme/component';

/**
 * Accordion behaviour for the mobile navigation drawer.
 *
 * Tapping a `.nav-drawer__row[aria-expanded]` toggles its `.nav-drawer__content` (animated with CSS grid rows).
 * Only one item is open at a time and everything is reset when the drawer closes.
 *
 * @extends {Component}
 */
class NavDrawer extends Component {
  /** @type {HTMLDetailsElement | null} */
  #details = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#onClick);

    this.#details = this.closest('details');
    this.#details?.addEventListener('toggle', this.#onToggle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#onClick);
    this.#details?.removeEventListener('toggle', this.#onToggle);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const row = event.target.closest('.nav-drawer__row[aria-expanded]');
    if (!(row instanceof HTMLElement) || !this.contains(row)) return;

    const item = row.closest('.nav-drawer__item');
    if (!(item instanceof HTMLElement)) return;

    if (item.classList.contains('nav-drawer__item--open')) {
      this.#close(item);
    } else {
      this.#open(item);
    }
  };

  /**
   * Reset every open row once the drawer has closed.
   */
  #onToggle = () => {
    if (this.#details?.open) return;

    for (const item of this.querySelectorAll('.nav-drawer__item--open')) {
      this.#close(item);
    }
  };

  /**
   * @param {HTMLElement} item
   */
  #open(item) {
    // Only one open item per level: close siblings (and their descendants), keep ancestors open
    const parent = item.parentElement;
    if (parent) {
      for (const sibling of parent.querySelectorAll(':scope > .nav-drawer__item--open')) {
        if (sibling !== item) this.#close(sibling);
      }
    }

    item.classList.add('nav-drawer__item--open');
    item.querySelector(':scope > .nav-drawer__row')?.setAttribute('aria-expanded', 'true');

    // Keep the opened row in view once the content has expanded
    setTimeout(() => {
      if (!item.classList.contains('nav-drawer__item--open')) return;
      item.querySelector(':scope > .nav-drawer__row')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 320);
  }

  /**
   * @param {Element} item
   */
  #close(item) {
    item.classList.remove('nav-drawer__item--open');
    item.querySelector(':scope > .nav-drawer__row')?.setAttribute('aria-expanded', 'false');

    for (const nested of item.querySelectorAll('.nav-drawer__item--open')) {
      nested.classList.remove('nav-drawer__item--open');
      nested.querySelector(':scope > .nav-drawer__row')?.setAttribute('aria-expanded', 'false');
    }
  }
}

if (!customElements.get('nav-drawer')) {
  customElements.define('nav-drawer', NavDrawer);
}
